import React from 'react';
import { Download, RefreshCw, Loader } from 'lucide-react';
import CSVCard from './CSVCard';
import SimpleBatchActions from './SimpleBatchActions';

interface CSVData {
  headers: string[];
  rows: any[];
  mappedHeaders: { [key: string]: string };
}

interface EditorStepProps {
  csvData: CSVData | null;
  regeneratingIndices: Set<number>;
  isBatchRegenerating: boolean;
  isCheckingCreditsForBatch: boolean;
  isCheckingCreditsForIndividual: boolean;
  onExportClick: () => void;
  onBatchRegenerateClick: () => void;
  onUpdateCSVData: (index: number, field: string, value: string) => void;
  onBatchUpdateAll: (updatedRows: any[]) => void;
  onRegenerate: (index: number) => void;
}

const EditorStep: React.FC<EditorStepProps> = ({
  csvData,
  regeneratingIndices,
  isBatchRegenerating,
  isCheckingCreditsForBatch,
  isCheckingCreditsForIndividual,
  onExportClick,
  onBatchRegenerateClick,
  onUpdateCSVData,
  onBatchUpdateAll,
  onRegenerate
}) => {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">CSV Editor</h1>
            <p className="text-gray-600 mt-2">
              Edit metadata for {csvData?.rows.length || 0} files
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onExportClick}
              disabled={!csvData}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={onBatchRegenerateClick}
              disabled={isBatchRegenerating || regeneratingIndices.size > 0 || isCheckingCreditsForBatch}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isBatchRegenerating ? (
                <Loader className="w-5 h-5 mr-2 animate-spin" />
              ) : regeneratingIndices.size > 0 ? (
                <Loader className="w-5 h-5 mr-2 animate-spin" />
              ) : isCheckingCreditsForBatch ? (
                <Loader className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5 mr-2" />
              )}
              {isBatchRegenerating ? 'Regenerating All...' : isCheckingCreditsForBatch ? 'Checking Credits...' : 'Regenerate All'}
            </button>
          </div>
        </div>
      </div>

      {/* Batch Actions */}
      {csvData && csvData.rows.length > 0 && (
        <SimpleBatchActions
          csvData={csvData}
          onUpdateAll={onBatchUpdateAll}
        />
      )}

      <div className="space-y-6">
        {csvData?.rows.map((row, index) => (
          <CSVCard
            key={index}
            row={row}
            index={index}
            isRegenerating={regeneratingIndices.has(index)}
            isCheckingCreditsForIndividual={isCheckingCreditsForIndividual}
            onRegenerate={onRegenerate}
            onUpdate={onUpdateCSVData}
          />
        ))}
      </div>
    </div>
  );
};

export default EditorStep;
